'use client';

import { useAppRouter as useRouter } from '@/lib/navigation';
import { TableCell, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  getHistoricProcessStatePresentation,
  isHistoricProcessActive,
} from '@/lib/camunda/history-process-state';
import type { HistoryItem } from '@/lib/camunda/types';
import { cn } from '@/lib/utils';
import { getHistoryAccessLabel } from '@/lib/camunda/history-access';
import {
  TEXT_TOOLTIP_COLUMN_IDS,
  getCellClassName,
  type UnifiedColumn,
} from './history-table-column-utils';
import { HistoryCellContent } from './history-cell-content';

interface HistoryTableRowProps {
  item: HistoryItem;
  orderedColumns: UnifiedColumn[];
}

function getHistoryCellTooltip(item: HistoryItem, columnId: string): string | undefined {
  switch (columnId) {
    case 'titulo':
      return item.titulo ?? '-';
    case 'processDefinitionName':
      return item.processDefinitionName;
    case 'processInstanceTitle':
      return item.processInstanceTitle ?? '-';
    case 'taskName':
      return item.taskName;
    case 'assignee':
      return item.assignee ? item.assignee : isHistoricProcessActive(item.state) ? 'Por asignar' : '-';
    case 'initiator':
      return item.startUserName || '-';
    default:
      return undefined;
  }
}

export function HistoryTableRow({
  item,
  orderedColumns,
}: HistoryTableRowProps) {
  const router = useRouter();
  const initiatorName = item.startUserName || '-';

  const navigateToProcessHistory = () => {
    router.push(`/task-list/participated/${item.processInstanceId}`);
  };

  const renderCellContent = (column: UnifiedColumn) => {
    switch (column.id) {
      case 'titulo':
        return item.titulo ?? '-';
      case 'processDefinitionName':
        return item.processDefinitionName;
      case 'processInstanceTitle':
        return item.processInstanceTitle ?? '-';
      case 'taskName':
        return item.taskName;
      case 'assignee':
        return item.assignee ? item.assignee : isHistoricProcessActive(item.state) ? 'Por asignar' : '-';
      case 'startTime':
        return new Date(item.startTime).toLocaleDateString();
      case 'endTime':
        return item.endTime ? new Date(item.endTime).toLocaleDateString() : '-';
      case 'state': {
        const { label, className } = getHistoricProcessStatePresentation(item.state);
        return (
          <Badge variant="outline" className={cn('max-w-full truncate text-[11px]', className)}>
            {label}
          </Badge>
        );
      }
      case 'initiator':
        return initiatorName === '-' ? (
          <span className="text-gray-500">{initiatorName}</span>
        ) : (
          <Badge variant="outline" className="max-w-full truncate bg-blue-50 text-[11px] text-blue-700 border-blue-200">
            {initiatorName}
          </Badge>
        );
      case 'accessReasons':
        return (
          <Badge variant="outline" className="max-w-full truncate text-[11px]">
            {getHistoryAccessLabel(item.accessReasons)}
          </Badge>
        );
      default:
        return '-';
    }
  };

  return (
    <TableRow>
      {orderedColumns.map((column, columnIndex) => {
        const cellContent = renderCellContent(column);
        const cellTitle = TEXT_TOOLTIP_COLUMN_IDS.has(column.id)
          ? getHistoryCellTooltip(item, column.id)
          : undefined;

        return (
          <TableCell key={column.id} className={getCellClassName(column)}>
            <HistoryCellContent
              column={column}
              content={<button
                type="button"
                className="block w-full cursor-pointer appearance-none border-0 bg-transparent p-0 text-left font-inherit text-inherit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={navigateToProcessHistory}
                tabIndex={columnIndex === 0 ? 0 : -1}
                aria-label={columnIndex === 0 ? `Abrir historial del proceso ${item.processInstanceId}` : undefined}
              >
                {cellContent}
              </button>}
              title={cellTitle}
            />
          </TableCell>
        );
      })}
    </TableRow>
  );
}
